import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { BottomSheet } from '@/components/ui/BottomSheet'
import {
  resolveHeaderActions,
  type PageAction,
  type PageHeaderActionsProps,
} from './pageActions'

type ButtonVariant = 'primary' | 'outline' | 'error' | 'ghost'

interface ActionButtonProps {
  action: PageAction
  variant: ButtonVariant
  outline?: boolean
  block?: boolean
  className?: string
  /** Called after the action fires / on a nav click (used to close the sheet). */
  onSelect?: () => void
  /** Desktop keeps the compact `sm`; the mobile bar uses `md` for a thumb-sized tap target. */
  size?: 'sm' | 'md'
}

/**
 * Render one PageAction as the right element for its target:
 * `to` → <Link>, `href` → <a>, else a <button>. Icon/loading/disabled/testId
 * are applied uniformly.
 */
function ActionButton({ action, variant, outline, block, className = '', onSelect, size = 'sm' }: ActionButtonProps) {
  const common = {
    variant,
    outline,
    block,
    size,
    leftIcon: action.icon ? <action.icon className="w-4 h-4" /> : undefined,
    loading: action.loading,
    disabled: action.disabled,
    title: action.title,
    className,
    'data-testid': action.testId ?? `header-action-${action.key}`,
  }

  if (action.to !== undefined) {
    return (
      <Button as={Link} to={action.to} onClick={onSelect} {...common}>
        {action.label}
      </Button>
    )
  }
  if (action.href !== undefined) {
    return (
      <Button
        as="a"
        href={action.href}
        download={action.download}
        target={action.external ? '_blank' : undefined}
        rel={action.external ? 'noopener noreferrer' : undefined}
        onClick={onSelect}
        {...common}
      >
        {action.label}
      </Button>
    )
  }
  // onClick branch (union narrowed: to/href are undefined here).
  return (
    <Button
      type="button"
      onClick={() => {
        onSelect?.()
        action.onClick()
      }}
      {...common}
    >
      {action.label}
    </Button>
  )
}

/**
 * EP-106 Item 1 — the header action zone. Desktop keeps a flat inline row
 * (`[…menu as outline] [solid as primary]`); mobile collapses to the locked
 * invariant `[ solid ] [ ⋯ ]`, where `⋯` opens a bottom action sheet (per the
 * mobile-overflow decision — a sheet, not a dropdown). Falls back to null when
 * nothing is visible.
 */
export function PageHeaderActions({ primaryAction, overflowActions, collapseOverflow }: PageHeaderActionsProps) {
  const { t: tc } = useTranslation('common')
  const [sheetOpen, setSheetOpen] = useState(false)

  // The bar publishes its own rendered height as --page-action-bar-height on
  // the root element. Everything that has to sit on top of it (the Pagination
  // bar) or clear it (MainLayout's <main> bottom padding) reads that variable.
  //
  // It used to be a hardcoded `calc(3.5rem + env(safe-area-inset-bottom))`
  // repeated at 10 call sites, and it was simply wrong: the real bar is
  // py-2 (8px) + a btn-sm row (~32px) + the safe-area inset, so on a notched
  // iPhone the constant overshot by ~16px and left a visible gap between the
  // pagination bar and this one. A constant can't track button size, Thai label
  // wrapping or the inset, so measure instead of guessing.
  const barRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = barRef.current
    const root = document.documentElement
    if (!el) {
      root.style.setProperty('--page-action-bar-height', '0px')
      return
    }
    const publish = () => {
      // offsetHeight is 0 while the bar is display:none (sm and up), which is
      // exactly the value desktop wants.
      root.style.setProperty('--page-action-bar-height', `${el.offsetHeight}px`)
    }
    publish()
    const ro = new ResizeObserver(publish)
    ro.observe(el)
    return () => {
      ro.disconnect()
      // Leaving a stale height behind would pad the next page for a bar it has
      // no longer got.
      root.style.setProperty('--page-action-bar-height', '0px')
    }
  })
  const { solid, menu } = resolveHeaderActions(primaryAction, overflowActions)

  if (!solid) return null

  const hasMenu = menu.length > 0
  // Mobile: a single overflow item renders directly UNLESS the caller opts to
  // always collapse (long labels). 2+ always collapse to the Options sheet.
  const showSheet = collapseOverflow ? menu.length >= 1 : menu.length > 1
  const showDirect = !collapseOverflow && menu.length === 1
  const nonDestructive = menu.filter((a) => !a.destructive)
  const destructive = menu.filter((a) => a.destructive)
  const closeSheet = () => setSheetOpen(false)

  return (
    // `contents` on mobile so this wrapper is not a flex item of PageHeader:
    // everything it renders there is either display:none (the desktop row) or
    // fixed (the bar), so as a real box it was an EMPTY child that still made
    // PageHeader spend its gap-4 on it — 16px of dead space between the page
    // heading and the first card. `sm:block` restores it as a normal flex item
    // on desktop, where the inline row genuinely occupies it.
    <div className="contents sm:block sm:shrink-0">
      {/* Desktop — flat inline row: menu (outline) then solid (primary). */}
      <div className="hidden sm:flex sm:flex-wrap items-center justify-end gap-2">
        {menu.map((a) => (
          <ActionButton
            key={a.key}
            action={a}
            variant={a.destructive ? 'error' : 'outline'}
            outline={a.destructive || undefined}
          />
        ))}
        <ActionButton action={solid} variant="primary" />
      </div>

      {/* Mobile — fixed bottom action bar (EP-125), replacing the old in-header
          row.
          `whitespace-nowrap` + `overflow-x-auto` on the bar: a long Thai label
          must never mid-word wrap onto a second line — it scrolls horizontally
          instead (same invariant as the old inline row).
          z-30 is the same slot as the Pagination sticky bar; BottomSheet (z-[70])
          and the drawer backdrop (z-40) both open above it. */}
      {/* NO in-flow spacer here. This component renders inside PageHeader, at
          the TOP of the page, so an in-flow spacer opened a blank band directly
          under the page title while giving the bar at the bottom no clearance
          at all. Clearance now comes from MainLayout's <main>, which pads by
          --page-action-bar-height. */}
      {/* Fixed bar — mirrors the Pagination stickyOnMobile recipe.
          Bottom padding is inset + 1rem, not max(inset, 0.5rem): the buttons
          need breathing room BELOW them as well as clearance of the home
          indicator, and max() collapses those into one.
          The top border is suppressed while a Pagination bar rests on this one:
          the divider marks where the bar STACK begins, so drawing it here too
          cut a line straight through the middle of the stack. Pagination sets
          --pagination-bar-present to 1, which zeroes the width below. */}
      <div
        ref={barRef}
        data-testid="page-action-bar"
        className="fixed inset-x-0 bottom-0 z-30 sm:hidden flex items-center gap-2 overflow-x-auto overflow-y-hidden no-scrollbar border-t-[calc(1px_-_1px_*_var(--pagination-bar-present,0))] border-base-200 bg-base-100/95 px-4 py-2 pb-[calc(env(safe-area-inset-bottom)+1rem)] backdrop-blur"
      >
        {showDirect && (
          <ActionButton
            action={menu[0]}
            variant={menu[0].destructive ? 'error' : 'outline'}
            outline={menu[0].destructive || undefined}
            size="md"
            className="shrink-0 whitespace-nowrap"
          />
        )}
        {showSheet && (
          <Button
            type="button"
            variant="outline"
            aria-haspopup="menu"
            aria-expanded={sheetOpen}
            data-testid="header-actions-overflow"
            onClick={() => setSheetOpen(true)}
            className="shrink-0 whitespace-nowrap"
          >
            {tc('labels.options')}
          </Button>
        )}
        <ActionButton
          action={solid}
          variant="primary"
          size="md"
          className={hasMenu ? 'flex-1 shrink-0 whitespace-nowrap' : 'ml-auto whitespace-nowrap'}
        />
      </div>

      {/* Mobile overflow → bottom action sheet. */}
      {showSheet && (
        <BottomSheet isOpen={sheetOpen} onClose={closeSheet} ariaLabel={tc('labels.actions_menu')}>
          <div className="flex flex-col gap-1">
            {nonDestructive.map((a) => (
              <ActionButton
                key={a.key}
                action={a}
                variant="ghost"
                block
                className="justify-start"
                onSelect={closeSheet}
              />
            ))}
            {destructive.length > 0 && nonDestructive.length > 0 && (
              <div className="my-1 border-t border-base-200" aria-hidden="true" />
            )}
            {destructive.map((a) => (
              <ActionButton
                key={a.key}
                action={a}
                variant="ghost"
                block
                className="justify-start text-error"
                onSelect={closeSheet}
              />
            ))}
          </div>
        </BottomSheet>
      )}
    </div>
  )
}
